/**
 * The palette's result layout, as data: what goes in which section, in what
 * order. Pure, so the order a person sees is decided in one tested place.
 *
 * One ranked list, every type mixed. The ranking (exact name, name prefix,
 * every word in the name, then the body, then typos) is what orders rows; the
 * type is said by each row's icon, not by a section. Grouping by type used to
 * hide that order and to reshuffle sections between keystrokes.
 *
 *   - All: the top rows of the Workspace list, a "Show all" into the
 *     Workspace tab, then a few meaning matches as Related.
 *   - Workspace tab: the same list in full, growing only at the bottom as it
 *     is scrolled (no section above the row being read ever changes).
 *
 * Sections follow the palette's scopes (Workspace, Chats, Go to, Settings,
 * Help), never entity types.
 */
import { searchWords } from '@bower/shared/constants/searchMatch';
import type { SearchResult } from '../../../services/search.service';

/** Workspace rows the All view shows before "Show all". */
export const WORKSPACE_PREVIEW = 6;
/** Meaning matches shown under a list; more say less than the ranked rows. */
export const RELATED_MAX = 3;
/**
 * Which meaning matches the palette shows, from staging measurements on
 * 2026-10-21 (16 queries): right answers scored 0.71 to 0.88, unrelated rows
 * 0.60 to 0.69, and every one-word query's best row (an acronym like "PCR"
 * at 0.684) sat below 0.70. So the band shows nothing unless the best match
 * reaches `RELATED_MIN_BEST`, and then only rows within
 * `RELATED_SIMILARITY_SPAN` of it ("minus 80 freezer manual": the manual
 * 0.714, the next row 0.654).
 *
 * Applies to the palette and to phone search's meaning-only rows. Agents
 * (`unified_search`) keep the server's 0.5 floor: they read excerpts and flag
 * weak matches.
 */
export const RELATED_MIN_BEST = 0.7;
export const RELATED_SIMILARITY_SPAN = 0.05;

/**
 * The meaning matches worth showing a person, by the rule above. `meaning` is
 * best first, each `rank` its similarity.
 */
export function meaningMatchesWorthShowing(
  meaning: readonly SearchResult[],
): readonly SearchResult[] {
  const best = meaning[0]?.rank ?? 0;
  if (best < RELATED_MIN_BEST) return [];
  return meaning.filter((r) => r.rank >= best - RELATED_SIMILARITY_SPAN);
}

/**
 * A `hybrid` answer for a person: every keyword match, and its meaning-only
 * rows (`match.field === 'meaning'`) only by the rule above, so a query that
 * matches nothing shows nothing rather than its nearest unrelated rows.
 */
export function withoutWeakMeaningMatches<T extends SearchResult>(results: readonly T[]): T[] {
  const kept = new Set(
    meaningMatchesWorthShowing(results.filter((r) => r.match?.field === 'meaning')).map(
      (r) => r.id,
    ),
  );
  return results.filter((r) => r.match?.field !== 'meaning' || kept.has(r.id));
}

export interface PaletteSections {
  /** Ranked Workspace rows on screen. */
  readonly rows: readonly SearchResult[];
  /** Workspace matches in all; above `rows.length` means "Show all". */
  readonly total: number;
  /** Meaning matches not already among `rows`, the rows on screen. */
  readonly related: readonly SearchResult[];
}

/**
 * @param results the ranked Workspace rows loaded so far, in server order
 * @param total how many Workspace matches the server counted
 * @param cap rows to show (the All preview), or undefined for every row
 */
export function buildPaletteSections(
  results: readonly SearchResult[],
  meaning: readonly SearchResult[],
  total: number,
  cap?: number,
): PaletteSections {
  const rows = cap === undefined ? results : results.slice(0, cap);
  // Only what is on screen. Deduping against every loaded row hid a strong
  // meaning match that also sat at keyword row 7+ (a some-words match behind
  // "Show all"): it appeared nowhere on the All tab (staging E2E, 2026-10-21).
  const shown = new Set(rows.map((r) => r.id));
  // The two requests fold a run separately, so a run can come back from both
  // under different captures: one row per run on screen.
  const groupKey = (r: SearchResult) => (r.group ? `${r.group.kind}:${r.group.id}` : undefined);
  const shownGroups = new Set(rows.map(groupKey).filter((key) => key !== undefined));
  const related = meaningMatchesWorthShowing(meaning)
    .filter((r) => !shown.has(r.id) && !shownGroups.has(groupKey(r) ?? ''))
    .slice(0, RELATED_MAX);
  return { rows, total: Math.max(total, results.length), related };
}

/** A word of a title, with its position (the split rule of `searchWords`). */
const WORD = /[\p{L}\p{N}]+/gu;

/**
 * Character ranges of `title` to emphasise for `query`: each query word where
 * a word of the title STARTS with it, the way the server's name match reads a
 * word (prefix, case-insensitive). Never inside a word: the server does not
 * match there, so a mark there would claim a match that did not happen.
 */
export function highlightRanges(title: string, query: string): Array<[number, number]> {
  const terms = searchWords(query);
  if (terms.length === 0) return [];
  const ranges: Array<[number, number]> = [];
  for (const word of title.matchAll(WORD)) {
    const text = word[0].toLowerCase();
    const longest = terms.filter((t) => text.startsWith(t)).sort((a, b) => b.length - a.length)[0];
    if (longest && word.index !== undefined) {
      ranges.push([word.index, word.index + longest.length]);
    }
  }
  return ranges;
}
